/** A legend row and the grid it sits in. */
import type { JSX, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { NAMESPACES } from "@/i18n/constants";
import { cn } from "@/utils/class-name";
import { formatReadingValue } from "../../format";
import type { GetDisplayInfo } from "../../units/use-get-display-info";
import {
    DIMMED_SWATCH_OPACITY,
    FOCUSED_ROW_BACKGROUND,
    INK,
    TEXT_SECONDARY,
    TEXT_STALE,
    TEXT_TERTIARY,
} from "../panel-tokens";
import type { ReadoutRow } from "./readout";
import { formatAgo } from "./readout-format";

/**
 * Swatch and name, sensor, value. 236px is a 200px name behind the 16px indent, the swatch and its gap.
 * Chrome gives the value its full width first, so the sensor keeps its longest word and, below 300px, the name's cap is fixed.
 */
export const LEGEND_COLUMNS =
    "grid-cols-[fit-content(236px)_minmax(min-content,1fr)_auto] @max-[300px]:grid-cols-[minmax(min-content,236px)_minmax(0,1fr)_auto]";

const HEADED_INDENT = "16px";

function Value({ row, symbol, isDimmed }: { row: ReadoutRow; symbol: string; isDimmed: boolean }) {
    const { t: tCommon } = useTranslation(NAMESPACES.COMMON);
    const { value } = row;
    const numberColor = isDimmed ? TEXT_TERTIARY : INK;

    if (value === null) return <span style={{ color: numberColor }}>—</span>;

    const number = formatReadingValue(value);
    const unit = symbol === "" ? "" : ` ${symbol}`;

    // Two unbreakable pieces, so on a narrow panel "(2d ago)" drops under the reading instead of overflowing.
    if (row.staleForMs !== null) {
        return (
            <span style={{ color: TEXT_STALE }}>
                <span className={"whitespace-nowrap"}>{`${number}${unit}`}</span>{" "}
                <span className={"whitespace-nowrap"}>{`(${formatAgo(row.staleForMs, tCommon)})`}</span>
            </span>
        );
    }

    return (
        <span className={"whitespace-nowrap"}>
            <span className={"font-medium tabular-nums"} style={{ color: numberColor }}>
                {number}
            </span>
            {unit ? (
                <span className={"font-medium"} style={{ color: isDimmed ? TEXT_TERTIARY : TEXT_SECONDARY }}>
                    {unit}
                </span>
            ) : null}
        </span>
    );
}

/**
 * Below a 300px legend, every row moves its detail under its label and the label drops its 124px minimum.
 * A flush row has no heading over it, so its swatch starts at the legend's edge instead of the indent.
 */
export function LegendRow({
    row,
    label,
    detail,
    getDisplayInfo,
    isFocused,
    isDimmed,
    onToggleFocus,
    flush = false,
}: {
    row: ReadoutRow;
    label: ReactNode;
    /** Beside the label, or under it on a narrow legend. Nothing draws for an empty one. */
    detail: ReactNode;
    getDisplayInfo: GetDisplayInfo;
    isFocused: boolean;
    isDimmed: boolean;
    onToggleFocus: (streamId: number) => void;
    flush?: boolean;
}): JSX.Element {
    const { series } = row;

    return (
        <button
            type={"button"}
            aria-pressed={isFocused}
            onClick={() => onToggleFocus(series.streamId)}
            className={cn(
                "relative z-0 col-span-full grid grid-cols-subgrid items-center py-[4px] text-left leading-[14px]",
                // Hangs 6px past the swatch and the value; a pseudo-element so the grid tracks stay put.
                isFocused &&
                    "before:absolute before:inset-y-0 before:right-[-6px] before:left-[calc(var(--legend-indent)-6px)] before:-z-10 before:rounded-[6px] before:bg-[var(--focused-row-bg)] before:content-['']",
            )}
            style={{
                ["--focused-row-bg" as string]: FOCUSED_ROW_BACKGROUND,
                ["--legend-indent" as string]: flush ? "0px" : HEADED_INDENT,
            }}
        >
            <span
                className={"col-start-1 row-start-1 flex items-center gap-[8px] self-stretch pl-[var(--legend-indent)]"}
            >
                <span
                    aria-hidden
                    className={`w-[12px] flex-none rounded-[2px] ${series.seriesType === "bar" ? "h-[8px]" : "h-[3px]"}`}
                    style={{ backgroundColor: series.color, opacity: isDimmed ? DIMMED_SWATCH_OPACITY : undefined }}
                />
                <span
                    className={"min-w-[124px] text-[12px] @max-[300px]:min-w-0"}
                    style={{ color: isDimmed ? TEXT_TERTIARY : INK }}
                >
                    {label}
                </span>
            </span>
            {detail ? (
                <span
                    className={
                        // Under the label on a narrow legend: past the indent, the 12px swatch and its 8px gap.
                        "col-start-2 row-start-1 min-w-0 text-[11px] break-words @max-[300px]:col-[1/3] @max-[300px]:row-start-2 @max-[300px]:pt-[4px] @max-[300px]:pl-[calc(var(--legend-indent)+20px)]"
                    }
                    style={{ color: isFocused ? TEXT_SECONDARY : TEXT_TERTIARY }}
                >
                    {detail}
                </span>
            ) : null}
            <span className={"col-start-3 row-start-1 text-right text-[12px]"}>
                <Value row={row} symbol={getDisplayInfo(series.axisUnitCategory).symbol} isDimmed={isDimmed} />
            </span>
        </button>
    );
}
